import * as React from 'react';
import { Text } from './Text';

/**
 * "4:21" under an hour, "1:04:21" after. Never negative: a clock a second
 * ahead of the server's reads 0:00 rather than -0:01.
 */
export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = String(total % 60).padStart(2, '0');
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}`
    : `${minutes}:${seconds}`;
}

export interface ElapsedTimeProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'children' | 'color'> {
  /** When the clock started: a status change, a call going live. */
  since: Date | string | number;
}

/**
 * How long since something happened, ticking once a second: the timer on
 * the active-call pill and in the call's header.
 *
 *   <ElapsedTime since={call.statusChangedAt} />
 *
 * The only thing in the library with a clock of its own, because every
 * screen that shows a live call wants the same tick and the app would
 * otherwise re-render the whole screen once a second to get it.
 */
export const ElapsedTime = React.forwardRef<HTMLElement, ElapsedTimeProps>(
  function ElapsedTime({ since, ...props }, ref) {
    const start = new Date(since).getTime();
    const [now, setNow] = React.useState(() => Date.now());

    React.useEffect(() => {
      const timer = window.setInterval(() => setNow(Date.now()), 1000);
      return () => window.clearInterval(timer);
    }, []);

    // A missing or unreadable start is no clock, rather than "NaN:NaN" or a
    // throw from toISOString.
    if (Number.isNaN(start)) return null;

    // Tabular figures, so the pill does not twitch as the digits change;
    // the size and colour are whatever it sits in.
    return (
      <Text
        ref={ref}
        as="time"
        variant="inherit"
        numeric
        // Text's props are the common HTML ones; a <time>'s own goes in
        // through a spread.
        {...{ dateTime: new Date(start).toISOString() }}
        {...props}
      >
        {formatElapsed(now - start)}
      </Text>
    );
  }
);
